import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import axios from "axios";
import Topbar from "./Topbar";
import MatchDetailModal from "./MatchDetailModal";
import { createClient } from "@supabase/supabase-js";
import Leaderboard from "./Leaderboard";
import MatchReactions from "./MatchReactions";
import Footer from "./Footer";
import PlayerCard from "./PlayerCard";
import { useClickOutside } from "./useClickOutside";
import Loader from "./Loader";
import { UsersRound, Calendar } from "lucide-react";
import UpcomingFixtures from "./UpcomingFixtures";
import TimeRemain from "./TimeRemain";
import MatchupRewardModal from "./MatchupRewardModal";
import ConfettiAnimation from "./ConfettiAnimation";
import AnnouncementCenter from "./AnnouncementCenter";
// import RegionWeeklyLeaderboard from "./RegionWeeklyLeaderboard";
const apiBase = process.env.REACT_APP_API_URL;

const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

const Dashboard = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState();
  const [batchedReactions, setBatchedReactions] = useState({});
  const [batchedUserReactions, setBatchedUserReactions] = useState({});
  const [schedule, setSchedule] = useState([]);
  const [tournament, setTournament] = useState(null);
  const [standings, setStandings] = useState([]);
  const [matchups, setMatchups] = useState([]);
  const [matchupDetails, setMatchupDetails] = useState(null);
  const [leagueRewards, setLeagueRewards] = useState([]);
  const [myTeam, setMyTeam] = useState([]);
  const [fantasyTeam, setFantasyTeam] = useState(null);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [mergedStats, setMergedStats] = useState([]);
  const [totalBoostBonus, setTotalBoostBonus] = useState({});
  const [projectedPoints, setProjectedPoints] = useState([]);
  const [leagueStart, setLeagueStart] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const roleOrder = ["Top", "Jng", "Mid", "Bot", "Sup"];
  const [openCardId, setOpenCardId] = useState(null);
  const [showScoring, setShowScoring] = useState(false);
  const [reward, setReward] = useState(null);
  const [matchupReward, setMatchupReward] = useState(null);
  const [shownMatchupReward, setShownMatchupReward] = useState(false);
  const [shown, setShown] = useState(false);
  const ref = useRef(null);
  const [loading, setLoading] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const { leagueId } = useParams();
  // const [regionLeaderboard, setRegionLeaderboard] = useState(null);
  // const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  // const [showWeeklyLeaderboard, setShowWeeklyLeaderboard] = useState(false);
  const now = new Date();
  useClickOutside(ref, () => setOpenCardId(null));

  // helper
  const buildMergedStats = (team, teamStats) => {
    if (!team?.user_fantasy_team_players) return { mergedStats: [], totalBoostBonus: {} };

    const combined = team.user_fantasy_team_players
      .map((p) => {
        const stat = (teamStats || []).find((s) => s.player_id === p.player_id);

        // Make sure p.players exists
        if (!p.players) {
          console.warn("Missing player data for:", p.player_id);
          return null;
        }

        const mergedPlayer = {
          player_id: p.player_id,
          player_name: p.players.name,
          role: p.players.role,
          team_id: stat?.team_id ?? p.players.teams?.id,
          team_name: p.players.teams?.name ?? "",
          team_logo: p.players.teams?.team_logo ?? "",
          kills: stat?.kills ?? 0,
          assists: stat?.assists ?? 0,
          deaths: stat?.deaths ?? 0,
          base_points: stat?.base_points ?? 0,
          boost_name: stat?.boost_name ?? null,
          boost_bonus: stat?.boost_bonus ?? 0,
          boosted_points: stat?.boosted_points ?? 0,
          player_image: p.players.player_image,
          boost_item_image: stat?.boost_image ?? null,
        };
        return mergedPlayer;
      })
      .filter(Boolean); // remove any nulls if player data was missing

    // Boost bonuses aggregation
    const groupedBoostBonuses = {};
    combined.forEach((p) => {
      if (!p.boost_name || p.boost_name === "No Boost") return;
      const name = p.boost_name;
      if (!groupedBoostBonuses[name]) {
        groupedBoostBonuses[name] = {
          totalBonus: 0,
          itemImage: p.boost_item_image || "",
        };
      }
      // Use the actual difference so total_points_multiplier boosts show correctly
      const bonus = p.boosted_points - p.base_points || 0;
      groupedBoostBonuses[name].totalBonus += bonus;
    });

    return { mergedStats: combined, totalBoostBonus: groupedBoostBonuses };
  };

  const fetchMatchupDetails = useCallback(
    async (matchupId) => {
      try {
        const response = await axios.post(
          `${apiBase}/api/matchup/details`,
          { matchup_id: matchupId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setMatchupDetails(response.data);
      } catch (err) {
        console.error("Error fetching matchup details:", err.message);
      }
    },
    [token], // only changes if these change
  );

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      setIsLoadingTeam(true);
      setSelectedLeagueId(leagueId);
      // setLeaderboardLoading(true);
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session || !session.user) {
        console.error("User not logged in or session expired:", sessionError?.message);
        setLoading(false);
        await supabase.auth.signOut();
        window.location.href = "/login";
        return;
      }

      const token = session.access_token;
      setToken(token);

      try {
        const response = await axios.post(
          `${apiBase}/api/dashboard/data`,
          { league_id: leagueId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const { league, team, teamStats, schedule, standings, mergedMatchups, leagueStart, projectedPointsNextMatch, leagueRewards } = response.data;

        const filteredMatches = (schedule || []).filter((match) => match.games.some((game) => game.status === "live" || game.status === "upcoming"));

        // === Fetch reactions for all upcoming matches in batch ===
        const matchIds = filteredMatches.map((m) => m.id);

        if (matchIds.length > 0) {
          try {
            // Fetch reaction counts
            const countsRes = await axios.post(`${apiBase}/api/reactions/counts`, { match_ids: matchIds });
            setBatchedReactions(countsRes.data || {});

            // Fetch user's reactions
            const userRes = await axios.post(
              `${apiBase}/api/reactions/user`,
              { match_ids: matchIds },
              { headers: { Authorization: `Bearer ${token}` } },
            );
            setBatchedUserReactions(userRes.data || {});
          } catch (err) {
            console.error("Failed to fetch batched reactions:", err.message);
          }
        }

        setTournament(league);
        setFantasyTeam(team);
        setLeagueRewards(leagueRewards);
        setMyTeam(teamStats || []);
        setLeagueStart(leagueStart);
        setProjectedPoints(projectedPointsNextMatch || []);
        setSchedule(filteredMatches || []);
        setStandings(standings);
        setMatchups(mergedMatchups);

        const { mergedStats, totalBoostBonus } = buildMergedStats(team, teamStats);
        setMergedStats(mergedStats);
        setTotalBoostBonus(totalBoostBonus);
      } catch (err) {
        console.error("Error loading dashboard:", err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
        setIsLoadingTeam(false);
      }
    };

    loadDashboard();
  }, [leagueId]);

  useEffect(() => {
    if (!tournament) return;

    const now = new Date();
    const startDate = new Date(tournament.start_date);
    // const startDate = new Date(Date.now() - 60 * 60 * 1000); // 1 hour ago

    // Check if user already submitted a team
    const hasTeam = fantasyTeam?.user_fantasy_team_players?.length > 0;
    const hasStarted = now >= startDate;

    // Lock only if tournament started AND user has submitted a team
    const locked = now >= startDate && hasTeam;

    setIsLocked(locked);
    setHasStarted(hasStarted);
  }, [tournament, fantasyTeam]);

  useEffect(() => {
    if (!token || shown) return;

    const fetchReward = async () => {
      try {
        const { data } = await axios.get(`${apiBase}/api/rewards/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (data.reward) {
          setReward(data.reward);
          setShown(true); // 🔒 only show once
        }
      } catch (err) {
        console.error("Failed to fetch reward:", err.response?.data?.error || err.message);
      }
    };

    fetchReward();
  }, [token, shown]);

  useEffect(() => {
    if (!token || shownMatchupReward) return;

    const fetchMatchupReward = async () => {
      try {
        const { data, error } = await axios.get(`${apiBase}/api/rewards/ready`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { league_id: leagueId },
        });

        if (error) throw error;

        if (data.rewards && data.rewards.length > 0) {
          setMatchupReward(data.rewards[0]); // take the first unclaimed reward
          setShownMatchupReward(true);
        }
      } catch (err) {
        console.error("Failed to fetch matchup reward:", err.response?.data?.error || err.message);
      }
    };

    fetchMatchupReward();
  }, [token, shownMatchupReward, leagueId]);

  // const fetchRegionWeeklyLeaderboard = async (region) => {
  //   setLeaderboardLoading(true);
  //   try {
  //     const res = await axios.get(`${apiBase}/api/region/${region}/weekly-leaderboard`, {
  //       headers: { Authorization: `Bearer ${token}` },
  //     });

  //     setRegionLeaderboard(res.data);
  //   } catch (err) {
  //     console.error("Failed to fetch weekly leaderboard:", err.response?.data?.error || err.message);
  //     setRegionLeaderboard({ leaderboard: [], rewards: [] });
  //   } finally {
  //     setLeaderboardLoading(false);
  //   }
  // };

  // const openWeeklyLeaderboardModal = () => {
  //   setShowWeeklyLeaderboard(true);
  //   fetchRegionWeeklyLeaderboard(tournament.region);
  // };

  // const closeWeeklyLeaderboardModal = () => {
  //   setShowWeeklyLeaderboard(false);
  // };

  const hasMissingBoosts = useMemo(() => {
    return fantasyTeam?.user_fantasy_team_players?.some((p) => !p.boosts || p.boosts.id === "no_boost");
  }, [fantasyTeam]);

  function getNextCronTick(date = new Date()) {
    const next = new Date(date);
    const minutes = next.getMinutes();
    const remainder = (15 - (minutes % 15)) % 15;
    next.setMinutes(minutes + remainder);
    next.setSeconds(0);
    next.setMilliseconds(0);
    return next;
  }

  const leagueStartWeek = leagueStart?.[0];

  const matchupGenerationAt = useMemo(() => {
    if (!leagueStartWeek?.week_start_at) return null;

    const weekStartAt = new Date(leagueStartWeek.week_start_at);

    const generateAt = new Date(weekStartAt.getTime() - 1 * 60 * 60 * 1000); // 1 hour before

    return getNextCronTick(generateAt);
  }, [leagueStartWeek]);

  const leagueState = useMemo(() => {
    if (!matchups || matchups.length === 0) return "preseason";

    const nowTs = Date.now();

    const firstStart = new Date(matchups[0].week_start_at).getTime();
    const lastEnd = new Date(matchups[matchups.length - 1].week_end_at).getTime();

    if (nowTs < firstStart) return "preseason";
    if (nowTs > lastEnd) return "completed";
    return "live";
  }, [matchups]);

  const featured = useMemo(() => {
    if (leagueState !== "live") return null;
    if (!matchups || matchups.length === 0) return null;

    const nowTs = Date.now();

    const nextMatchup = matchups.find((m) => new Date(m.week_end_at).getTime() > nowTs);

    if (!nextMatchup) return null;

    const weekNumber = matchups.findIndex((m) => m.id === nextMatchup.id) + 1;
    return { ...nextMatchup, weekNumber };
  }, [matchups, leagueState]);

  const upcomingFixtures = useMemo(() => {
    if (!matchups || matchups.length === 0) return [];

    // Only matchups that involve the user
    const userMatchups = matchups.filter((m) => m && m.week_start && (m.home_team_id === fantasyTeam?.id || m.away_team_id === fantasyTeam?.id));

    // Only future matchups
    // const future = userMatchups.filter((m) => new Date(m.week_start) > todayDate).sort((a, b) => new Date(a.week_start) - new Date(b.week_start));

    return userMatchups.map((m) => ({
      ...m,
      weekNumber: matchups.findIndex((x) => x.id === m.id) + 1,
    }));
  }, [matchups, fantasyTeam]);

  useEffect(() => {
    if (featured?.id && token) {
      fetchMatchupDetails(featured.id);
      // setWeekStart(featured.week_start);
      // setWeekEnd(featured.week_end);
    }
  }, [featured, token, fetchMatchupDetails]);

  const podium = useMemo(() => {
    if (!standings || standings.length === 0) return [];

    const first = standings.find((s) => s.league_rank === 1);
    const second = standings.find((s) => s.league_rank === 2);
    const third = standings.find((s) => s.league_rank === 3);

    // Explicit podium order: 2 - 1 - 3
    return [second, first, third].filter(Boolean);
  }, [standings]);

  const rewardsByPlacement = useMemo(() => {
    if (!leagueRewards) return {};

    return leagueRewards.reduce((acc, r) => {
      acc[r.placement] = r;
      return acc;
    }, {});
  }, [leagueRewards]);

  const formatMatchInfo = (dateTimeString) => {
    const matchDate = new Date(dateTimeString);

    // Truncate to midnight for comparison (user's local time)
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const matchDay = new Date(matchDate.getFullYear(), matchDate.getMonth(), matchDate.getDate());

    const msInDay = 1000 * 60 * 60 * 24;
    const dayDiff = Math.round((matchDay - today) / msInDay);

    // Label logic
    let dayLabel;
    if (dayDiff === 0) {
      dayLabel = "TODAY";
    } else if (dayDiff === 1) {
      dayLabel = "TOMORROW";
    } else {
      dayLabel = matchDate.toLocaleDateString("en-US").toUpperCase();
    }

    // Time formatting in local timezone
    const timeString = matchDate
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toUpperCase();

    return {
      dayLabel,
      time: timeString,
    };
  };

  const getLiveOpponentInfo = (player, schedule) => {
    if (!player || !player.team_id) return { isLive: false };

    for (const match of schedule) {
      for (const game of match.games) {
        if (game.status === "live") {
          if (game.blue_team_id === player.team_id) {
            return { isLive: true };
          }
          if (game.red_team_id === player.team_id) {
            return { isLive: true };
          }
        }
      }
    }

    return { isLive: false };
  };

  const isLiveWeek = (week_start_at) => {
    if (!week_start_at) return false;
    return new Date() >= new Date(week_start_at);
  };

  const live = featured ? isLiveWeek(featured.week_start_at) : false;

  const getTeamRecord = (teamId) => {
    const record = standings.find((s) => s.fantasy_team_id === teamId);
    if (!record) return "0W - 0L";
    return `${record.wins}W - ${record.losses}L${record.ties > 0 ? ` - ${record.ties}T` : ""}`;
  };

  const getTeamRank = (teamId) => {
    const record = standings.find((s) => s.fantasy_team_id === teamId);
    if (!record) return "";
    return record.league_rank;
  };

  const userTeamId = fantasyTeam?.id;
  const userStanding = standings?.find((s) => s.fantasy_team_id === userTeamId);

  const getProjectedWinner = (match) => {
    if (!match?.home_team || !match?.away_team) {
      return { winner: null, homeTotal: 0, awayTotal: 0 };
    }

    const sumProjected = (team) => {
      if (!team?.user_fantasy_team_players) return 0;
      return team.user_fantasy_team_players.reduce((total, p) => total + (p.h2h_projected || 0), 0);
    };

    const homeTotal = sumProjected(match.home_team);
    const awayTotal = sumProjected(match.away_team);

    let winner = "tie";
    if (homeTotal > awayTotal) winner = "home";
    else if (awayTotal > homeTotal) winner = "away";

    return { winner, homeTotal, awayTotal };
  };

  const leagueEnded = leagueState === "completed";

  const SuperscriptNumber = ({ value, color }) => {
    if (value === null || value === undefined || value === "-") {
      return <span>-</span>;
    }

    const [intPart, decimalPart] = Number(value).toFixed(2).split(".");

    return (
      <span style={{ display: "inline-flex", alignItems: "flex-start" }}>
        <span>{intPart}</span>
        <span
          style={{
            fontSize: "16px",
            marginLeft: "6px",
            position: "relative",
            top: "6px",
            opacity: 0.9,
            color,
          }}>
          .{decimalPart}
        </span>
      </span>
    );
  };

  const formatDateUTC = (dateStr) => {
    if (!dateStr) return "Invalid date";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleDateString(undefined, {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
    });
  };

  const collectReward = async () => {
    try {
      await axios.post(
        `${apiBase}/api/rewards/collect`,
        {
          source: reward.source,
          rewardId: reward.id ?? null,
          meta: reward.meta ?? null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setReward(null);
    } catch (err) {
      console.error("Failed to collect reward:", err.response?.data?.error || err.message);
    }
  };

  if (loading) return <Loader loadingTitle={"Loading dashboard"} showLogo={true} />;

  return (
    <div className="page-container">
      <Topbar leagueId={leagueId} showLinks={true} showBudget={true} />
      {/* <h4
        style={{
          padding: "2px 10px",
          justifyContent: "center",
          display: "flex",
          alignItems: "center",
          background: "#87001d",
          color: "white",
          fontSize: "14px",
          gap: 6,
          textDecoration: "underline",
          cursor: "pointer",
          borderRadius: "4px",
        }}
        onClick={() => navigate(`/rewards/${leagueId}`)}>
         <img src="/chinese-new-year-collection-title.png" style={{ height: "16px" }} alt="Chinese New Year Collection" />
        <MoveRight />
      </h4> */}
      <AnnouncementCenter />
      <div className="main">
        {reward && (
          <div className="modal-overlay" style={{ zIndex: "12000" }}>
            <div
              className="modal"
              style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative", maxWidth: "700px" }}>
              <img
                src={reward.banner ? reward.banner : `/many-tokens.png`}
                alt="Many League Tokens"
                style={{ position: "absolute", top: "-60px", height: " 100px" }}
              />
              <h2 style={{ color: "rgb(235, 195, 115)", textAlign: "center", marginTop: "36px", marginBottom: "0" }}>
                {reward.title || "Here's a gift for you!"}
              </h2>
              <p style={{ textAlign: "center", fontSize: "14px" }}>
                {reward.sub_message ? (
                  reward.sub_message
                ) : (
                  <>
                    You earned a one-time <b style={{ color: "rgb(240, 217, 125)" }}>{reward.reward_amount.toLocaleString()} Token Reward</b>
                  </>
                )}
              </p>
              <h4 style={{ textAlign: "center" }}>{reward.message || "We've recently fixed a bug or feature that affected your gameplay!"}</h4>
              <div
                style={{
                  background: "#010407",
                  borderRadius: "12px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "90%",
                  padding: "10px",
                  marginTop: "30px",
                }}>
                <img
                  src="/credit-token.png"
                  alt="Many League Tokens"
                  style={{
                    height: "64px",
                    objectFit: "contain",
                    filter: "drop-shadow(0 0 20px rgba(255, 255, 255, 0.2))",
                    zIndex: "99",
                  }}
                />
                <h4 style={{ fontWeight: "900", fontSize: "20px", marginTop: "-15px", zIndex: "100", textShadow: "0px 0px 8px rgba(0, 0, 0, 0.8)" }}>
                  x{reward.reward_amount.toLocaleString()}
                </h4>
              </div>
              <div style={{ display: "flex", flexDirection: " column", alignItems: " center", gap: 20, marginTop: "20px" }}>
                <button onClick={collectReward} className="edit-btn">
                  Collect
                </button>
              </div>
            </div>
          </div>
        )}
        {matchupReward && <MatchupRewardModal reward={matchupReward} token={token} teamId={fantasyTeam?.id} onClose={() => setMatchupReward(null)} />}
        {/* {showWeeklyLeaderboard && (
          <RegionWeeklyLeaderboard
            weekData={regionLeaderboard}
            loading={leaderboardLoading}
            onClose={closeWeeklyLeaderboardModal}
            timeRemain={featured.week_end_at}
          />
        )} */}
        <div style={{ display: "flex", gap: "20px", justifyContent: "space-between" }}>
          <div className="main-column">
            {/* <div style={{ width: "100%", padding: "4px", backgroundColor: "#3273fa", textAlign :"center", color :"white", fontWeight: "600"}}>OffMeta Fantasy is shutting down on June 20th. Thank you all for playing!</div> */}
            {schedule.length >= 1 ? (
              <div className="upcoming-matches-container">
                <h2>UPCOMING MATCHES &#8594;</h2>
                <div
                  style={{
                    position: "relative",
                  }}
                  className="fade-out-right">
                  <div className="scroll-content" style={{ overflow: "scroll", display: "flex", gap: "8px" }}>
                    <div
                      className="match-card"
                      onClick={() => navigate(`/dashboard/league/${leagueId}/history`)}
                      style={{ cursor: "pointer", border: "1px solid #19232f" }}>
                      <div
                        className="game-block"
                        style={{
                          borderRadius: "4px",
                          height: "100%",
                          boxSizing: "border-box",
                          alignItems: "center",
                        }}>
                        <Calendar color="rgb(190, 205, 227)" size={21} />
                      </div>
                    </div>
                    {schedule.map((match) => {
                      const { dayLabel, time } = formatMatchInfo(match.match_date);
                      const firstGame = match.games?.[0];
                      const isLive = match.games.some((game) => game.status === "live");
                      return (
                        <div
                          key={match.id}
                          className="match-card"
                          onClick={() => setSelectedMatch(match)}
                          style={{ cursor: "pointer", border: isLive ? "none" : "1px solid #19232f" }}>
                          <div
                            className="game-block"
                            style={{
                              border: isLive ? "1px solid #F14646" : "",
                              borderRadius: isLive ? "4px" : "",
                              transition: "background 0.3s ease",
                            }}>
                            <div className="teams">
                              <div className="teams-matchup-box">
                                <div className="team">
                                  <img src={`/teams/${firstGame?.blue_team?.team_logo}`} alt={firstGame?.blue_team?.name} className="team-logo-md" />
                                </div>
                                <div>
                                  {isLive ? (
                                    <span
                                      className="upcoming-match-info"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexDirection: "row",
                                        gap: "6px",
                                      }}>
                                      <span className="live-pulse" />
                                      <h3
                                        style={{
                                          opacity: "1",
                                          color: "white",
                                          fontSize: "12px",
                                        }}>
                                        LIVE
                                      </h3>
                                    </span>
                                  ) : (
                                    <span className="upcoming-match-info">
                                      <h3 style={{ opacity: ".6" }}>{dayLabel}</h3>
                                    </span>
                                  )}
                                  <span className="upcoming-match-info" style={{ display: "flex" }}>
                                    <h3 style={{ fontSize: "12px", color: "white" }}>{!isLive && `${time} | BO${match.best_of}`}</h3>
                                  </span>
                                </div>
                                <div className="team">
                                  <img src={`/teams/${firstGame?.red_team?.team_logo}`} alt={firstGame?.red_team?.name} className="team-logo-md" />
                                </div>
                              </div>

                              <div onClick={(e) => e.stopPropagation()}>
                                {!isLive && (
                                  <MatchReactions
                                    matchId={match.id}
                                    reactions={batchedReactions[match.id] || {}}
                                    selectedEmoji={batchedUserReactions[match.id] || null}
                                    accessToken={token}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <MatchDetailModal isOpen={!!selectedMatch} onClose={() => setSelectedMatch(null)} match={selectedMatch} />
                  </div>
                </div>
              </div>
            ) : (
              <></>
            )}
            <div className="main-row">
              <div
                className="featured-matchup-card"
                style={{ boxShadow: "inset 0 0 1rem 4px #3273fa", border: "1px solid rgba(50, 113, 250, 1)", padding: "20px 10px" }}>
                {/* {!leagueEnded && featured && (
                  <div
                    onClick={openWeeklyLeaderboardModal}
                    style={{ width: "fit-content", position: "absolute", top: "30px", right: "0px", zIndex: "100", cursor: "pointer" }}>
                    <div style={{ background: "white", borderRadius: "90px", padding: "2px 8px", textTransform: "uppercase", position: "relative" }}>
                      <span
                        style={{
                          position: "absolute",
                          top: "-78%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          border: "3px solid white",
                          borderRadius: "90px",
                          width: "45px",
                          height: "45px",
                          backgroundColor: "#f5c019",
                        }}>
                        <img
                          src="/weekly-title.png"
                          alt="Weekly Point Dash"
                          style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "32px" }}
                        />
                      </span>
                      {featured && (
                        <TimeRemain startDate={featured.week_end_at} fontColor={"#393640"} fontSize={"12px"} showClock={false} showSeconds={false} />
                      )}
                    </div>
                  </div>
                )} */}
                {/* <div className="background-grid" /> */}
                <div className="background-fade" />
                <img src="/matchday-bg.png" className="matchday-background" alt="Matchday Background" />
                {leagueState === "live" && featured ? (
                  <div className="teams-container">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "center",
                        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                        boxSizing: "border-box",
                        gap: "10px",
                      }}
                      id="featured-container-title">
                      <h2
                        style={{
                          fontWeight: "900",
                          color: "white",
                          letterSpacing: "-1px",
                          margin: "0",
                          fontSize: "22px",
                          fontStyle: "italic",
                          textAlign: "center",
                          textShadow: "0px 0px 8px rgb(0 0 0 / 40%)",
                        }}
                        className="tournament-stage-title">
                        Featured Matchup
                      </h2>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "10px",
                        marginBottom: "4px",
                      }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <h4
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              color: "white",
                              fontSize: "12px",
                              fontWeight: "600",
                            }}>
                            {tournament.region === "LCK" ? (
                              <>
                                <ReactCountryFlag
                                  countryCode="KR"
                                  svg
                                  cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                                  cdnSuffix="svg"
                                  title="South Korea"
                                  style={{ height: "16px", borderRadius: "90px", width: "16px" }}
                                />
                                Seoul,
                              </>
                            ) : tournament.region === "LEC" ? (
                              <>
                                <ReactCountryFlag
                                  countryCode="DE"
                                  svg
                                  cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                                  cdnSuffix="svg"
                                  title="Germany"
                                  style={{ height: "16px", borderRadius: "90px", width: "16px" }}
                                />
                                Berlin,
                              </>
                            ) : tournament.region === "LPL" ? (
                              <>
                                <ReactCountryFlag
                                  countryCode="CN"
                                  svg
                                  cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                                  cdnSuffix="svg"
                                  title="China"
                                  style={{ height: "16px", borderRadius: "90px", width: "16px" }}
                                />
                                Shanghai,
                              </>
                            ) : tournament.region === "LCP" ? (
                              <>
                                <ReactCountryFlag
                                  countryCode="TW"
                                  svg
                                  cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                                  cdnSuffix="svg"
                                  title="Taiwan"
                                  style={{ height: "16px", borderRadius: "90px", width: "16px" }}
                                />
                                Taipei,
                              </>
                            ) : (
                              tournament.name
                            )}
                          </h4>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <h4 style={{ color: "white", fontSize: "12px", fontWeight: "600" }}>
                            {tournament.region === "LCK"
                              ? "LoL Park"
                              : tournament.region === "LEC"
                                ? "Riot Games Arena"
                                : tournament.region === "LPL"
                                  ? "Shanghai League Arena"
                                  : tournament.region === "LCP"
                                    ? "LCP Arena"
                                    : tournament.name}
                          </h4>
                        </div>
                      </div>
                    </div>
                    {featured && !live && <TimeRemain startDate={featured.week_start_at} fontColor={"white"} />}
                    {featured && live && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          background: "rgb(0 0 0 / 40%)",
                          borderRadius: "90px",
                          padding: "4px 12px",
                          border: "1px solid #19232f",
                          boxShadow: "inset #0000004d 0 2px 4px",
                        }}>
                        <span className="live-pulse" />
                        <p
                          style={{
                            margin: "0",
                            fontSize: "11px",
                            opacity: "0.6",
                            fontWeight: "400",
                            color: "white",
                          }}>
                          MATCHWEEK IS LIVE |
                        </p>
                        <h2
                          style={{
                            margin: "0",
                            fontWeight: "600",
                            letterSpacing: "-0.4px",
                            fontSize: "12px",
                            textAlign: "center",
                            opacity: "0.6",
                          }}>
                          Week {featured.weekNumber} • {formatDateUTC(featured.week_start)} – {formatDateUTC(featured.week_end)}
                        </h2>
                      </div>
                    )}
                    <div className="matchup-teams-container">
                      <div className="home-matchup-container">
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexDirection: "column" }}>
                          <p style={{ color: "white", fontSize: "12px", opacity: "0.6", margin: "0" }}>#{getTeamRank(featured.home_team?.id)}</p>
                          <div style={{ position: "relative" }}>
                            <img src={`/avatars/${featured.home_team?.profile_image}`} alt="Profile" style={{ borderRadius: "8px" }} id="class-4" />
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <h3 style={{ color: "white", margin: "0", fontSize: "16px", fontWeight: "700" }}>{featured.home_team?.display_name}</h3>
                            <p style={{ color: "white", fontSize: "12px", opacity: "0.6", margin: "0" }}>{getTeamRecord(featured.home_team?.id)}</p>
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          {/* <p style={{ fontWeight: "400", fontSize: "10px", margin: "0", color: "#ffffff99", textAlign: "right" }}>pts</p> */}
                          <h2
                            style={{
                              color: "white",
                              margin: "0",
                              fontSize: "38px",
                              fontWeight: "800",
                              fontStyle: "italic",
                            }}>
                            <SuperscriptNumber value={matchupDetails?.home_points} color="white" />
                          </h2>

                          <h4
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              color: "#ffffff99",
                              fontSize: "13px",
                              fontWeight: "600",
                              justifyContent: "flex-end",
                            }}>
                            {getProjectedWinner(featured).homeTotal.toFixed(1)}
                          </h4>
                        </div>
                      </div>
                      <div className="pts-divider">
                        <img src="/vs-vector.png" style={{ width: "48px", opacity: "0.2" }} alt="VS" />
                      </div>
                      <div className="away-matchup-container">
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexDirection: "column" }}>
                          <p style={{ color: "white", fontSize: "12px", opacity: "0.6", margin: "0" }}>#{getTeamRank(featured.away_team?.id)}</p>
                          <img src={`/avatars/${featured.away_team?.profile_image}`} alt="Profile" style={{ borderRadius: "8px" }} id="class-4" />
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <h3 style={{ color: "white", margin: "0", fontSize: "16px", fontWeight: "700" }}>{featured.away_team?.display_name}</h3>
                            <p style={{ color: "white", fontSize: "12px", opacity: "0.6", margin: "0" }}>{getTeamRecord(featured.away_team?.id)}</p>
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          {/* <p style={{ fontWeight: "400", fontSize: "10px", margin: "0", color: "#ffffff99" }}>pts</p> */}
                          <h2
                            style={{
                              color: "white",
                              margin: "0",
                              fontSize: "38px",
                              fontWeight: "800",
                              fontStyle: "italic",
                            }}>
                            <SuperscriptNumber value={matchupDetails?.away_points} color="white" />
                          </h2>

                          <h4
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              color: "#ffffff99",
                              fontSize: "13px",
                              fontWeight: "600",
                              justifyContent: "flex-start",
                            }}>
                            {getProjectedWinner(featured).awayTotal.toFixed(1)}
                          </h4>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", justifyContent: "center" }}>
                      <button
                        onClick={() => navigate(`/dashboard/league/${leagueId}/matchup/${featured.id}`)}
                        className="edit-btn"
                        style={{ flex: live ? "1 1" : "" }}>
                        VIEW ARENA MATCHUP
                      </button>
                      {!live && (
                        <button
                          onClick={() => navigate(`/dashboard/league/${selectedLeagueId}/prep`)}
                          className="edit-btn"
                          style={{ backgroundColor: "#faae32" }}>
                          PRE-MATCH PREP
                        </button>
                      )}
                    </div>
                  </div>
                ) : leagueEnded ? (
                  <div style={{ zIndex: "10", position: "relative", maxWidth: "740px", margin: "auto" }}>
                    <ConfettiAnimation trigger={leagueEnded} />
                    <h2
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        fontWeight: "900",
                        color: "white",
                        letterSpacing: "-1px",
                        margin: "0",
                        fontSize: "22px",
                        fontStyle: "italic",
                        textAlign: "center",
                        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                        textShadow: "0px 0px 8px rgb(0 0 0 / 40%)",
                      }}
                      className="tournament-stage-title">
                      Final Results
                    </h2>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-end",
                        gap: "50px",
                        marginTop: "20px",
                      }}>
                      {podium.map((team) => {
                        const isFirst = team.league_rank === 1;
                        const isSecond = team.league_rank === 2;
                        const reward = rewardsByPlacement[team.league_rank];

                        return (
                          <div
                            key={team.fantasy_team_id}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              transform: isFirst ? "translateY(-14px)" : "none",
                            }}>
                            {/* Rank */}
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: 800,
                                color: isFirst ? "#FFD700" : isSecond ? "#C0C0C0" : "#CD7F32",
                                marginBottom: "6px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}>
                              {isFirst ? "Champion" : `#${team.league_rank}`}

                              {/* Record */}
                              <p
                                style={{
                                  fontSize: "11px",
                                  color: "#ffffff99",
                                  margin: 0,
                                }}>
                                {team.wins}W – {team.losses}L{team.ties > 0 && ` – ${team.ties}T`}
                              </p>
                              {/* Points */}
                              <p
                                style={{
                                  fontSize: "11px",
                                  color: "#ffffff66",
                                  margin: 0,
                                }}>
                                {team.total_points_scored.toFixed(1)} pts
                              </p>
                            </span>

                            {/* Avatar */}
                            <img
                              src={`/avatars/${team.profile_image}`}
                              alt={team.display_name}
                              style={{
                                width: isFirst ? "76px" : "60px",
                                height: isFirst ? "76px" : "60px",
                                borderRadius: "10px",
                                border: isFirst ? "" : "1px solid rgba(255,255,255,0.2)",
                                boxShadow: isFirst ? "0 0 14px rgba(255,215,0,0.4)" : "none",
                              }}
                              id={isFirst ? "class-3" : isSecond ? "class-4" : "class-5"}
                            />

                            {/* Name */}
                            <h4
                              style={{
                                margin: "8px 0 0",
                                fontSize: "13px",
                                fontWeight: 700,
                                color: "white",
                                textAlign: "center",
                              }}>
                              {team.display_name}
                            </h4>
                            {reward && (
                              <div
                                style={{
                                  marginTop: "6px",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: "6px",
                                  fontSize: "11px",
                                  color: "#ffffffcc",
                                }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  {/* Badge */}
                                  {reward.badge_image && (
                                    <img
                                      src={`/banners/${reward.badge_image}`}
                                      alt={reward.badge_name}
                                      title={reward.badge_name}
                                      style={{
                                        width: "28px",
                                        height: "28px",
                                        objectFit: "contain",
                                        marginBottom: "2px",
                                        filter: team.league_rank === 1 ? "drop-shadow(0 0 14px rgba(255, 255, 255, 0.6))" : "none",
                                      }}
                                    />
                                  )}
                                  {/* International Ticket */}
                                  {reward.grants_international_ticket && (
                                    <img
                                      src={reward.ticket_image || "/international-ticket.png"}
                                      alt="International Ticket"
                                      style={{ height: "28px" }}
                                    />
                                  )}
                                </div>
                                {/* Coins */}
                                {reward.coins > 0 && (
                                  <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#FFD700" }}>
                                    <img src="/credit-token.png" style={{ height: "21px" }} alt="Credit Token" /> {reward.coins}
                                  </span>
                                )}

                                {/* XP */}
                                {reward.xp > 0 && <span style={{ color: "#3273fa", fontSize: "12px" }}>✦ {reward.xp} XP</span>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="launch-countdown">
                      <div className="launch-countdown-heading">
                        <span className="launch-eyebrow">{tournament.name}</span>

                        <h2>
                          YOUR ROSTER. YOUR LEGACY.
                        </h2>

                      </div>
                      {leagueStartWeek && matchupGenerationAt && (
                        <TimeRemain startDate={matchupGenerationAt.toISOString()} subtitle="YOUR FIRST MATCHUP STARTS IN" fontColor={"white"} />
                      )}
                      <p
                        style={{
                          margin: "0",
                          fontSize: "12px",
                          opacity: "0.6",
                          fontWeight: "400",
                          color: "#a8c9ee",
                        }}>
                        THE LEAGUE WILL START SOON
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* <div className="hint-container" onClick={() => navigate(`/league/${selectedLeagueId}/train`)}>
              <div>
                <img src="/training-illustration.png" alt="Training Available" style={{ height: "64px" }} />
              </div>
              <div style={{ display: "flex", gap: "4px", flexDirection: "column" }}>
                <h2>PRACTICE &#8594;</h2>
                <p>INCREASE YOUR PLAYER SKILL, EARN % BONUS POINTS</p>
              </div>
            </div> */}
            <div className="main-row" id="mobile-main-row" style={{ marginTop: "20px" }}>
              {!isLoadingTeam ? (
                <>
                  <div className="selected-team-container">
                    <div className="league-info-container">
                      {tournament && (
                        <div>
                          <div style={{ display: "flex", alignItems: "flex-start", height: "min-content", gap: "6px", flexDirection: "column" }}>
                            <h2
                              className="tournament-stage-title"
                              style={{ fontWeight: "700", fontSize: "24px", fontStyle: "italic", maxWidth: "240px" }}>
                              {tournament.name}
                            </h2>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <p style={{ margin: "0", color: "white", opacity: "0.6", fontSize: "11px" }}>Powered by</p>
                              <img
                                src={`/logo.svg`}
                                alt={tournament.name}
                                className="tournament-banner-image"
                                style={{ width: "74px", height: "13px" }}
                              />
                            </div>
                          </div>
                          {standings && standings.length > 0 && (
                            <div
                              style={{
                                position: "relative",
                                marginTop: "20px",
                                background: "rgba(255, 255, 255, 0)",
                                padding: "10px 14px",
                                borderRadius: "8px",
                                display: "flex",
                                gap: "4px",
                                justifyContent: "space-between",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                fontSize: "14px",
                              }}>
                              <div className={`leaderboard-dot rank-${userStanding.league_rank}`}></div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  borderBottom: "1px solid #19232f",
                                  paddingBottom: "8px",
                                  width: "100%",
                                }}>
                                <img src={`/banners/${tournament.banner}`} alt={tournament.name} className="tournament-banner-image" />
                                <p style={{ color: "white", margin: "0" }}>&#x2022;</p>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}>
                                  <UsersRound size={16} color="#ffffff" />
                                  <p style={{ color: "white", margin: "0" }}>{standings.length}</p>
                                </div>
                              </div>
                              <div>
                                {userStanding ? (
                                  <div
                                    className="record-box"
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                      color: "#fff",
                                    }}>
                                    <div>
                                      <h2
                                        style={{
                                          fontWeight: "800",
                                          fontSize: "32px",
                                          margin: "0",
                                          letterSpacing: "-0.4px",
                                        }}>
                                        {userStanding.wins}
                                        <b style={{ fontWeight: "400", opacity: "0.6", fontSize: "21px" }}>W - </b>

                                        {userStanding.losses}
                                        <b style={{ fontWeight: "400", opacity: "0.6", fontSize: "21px" }}>L </b>

                                        {userStanding.ties > 0 && (
                                          <>
                                            <b style={{ fontWeight: 400, opacity: 0.6, fontSize: "21px" }}> - </b>
                                            {userStanding.ties}
                                            <b style={{ fontWeight: 400, opacity: 0.6, fontSize: "21px" }}>T</b>
                                          </>
                                        )}
                                      </h2>
                                    </div>
                                    <div style={{ fontSize: "12px", opacity: 0.8 }}>
                                      {(() => {
                                        const totalGames = (userStanding.wins ?? 0) + (userStanding.losses ?? 0) + (userStanding.ties ?? 0);
                                        return totalGames > 0 ? `${(((userStanding.wins ?? 0) / totalGames) * 100).toFixed(1)}% WR` : "0% WR";
                                      })()}
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ opacity: 0.6, marginTop: "8px" }}>No matches played yet</div>
                                )}
                              </div>
                            </div>
                          )}
                          {hasMissingBoosts && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                background: "rgba(255, 60, 60, 0.08)",
                                border: "1px dashed rgba(255, 100, 100, 0.5)",
                                borderRadius: "8px",
                                padding: "8px 14px",
                                fontSize: "12px",
                                color: "rgba(255, 120, 120, 0.9)",
                                fontWeight: "600",
                              }}>
                              Some players are missing an item boost.<br></br>Equip boosts in Pre-Match Prep to earn bonus points.
                            </div>
                          )}
                        </div>
                      )}
                      {(() => {
                        const hasNoPlayers = fantasyTeam?.user_fantasy_team_players?.length === 0;

                        // CASE 3: live → show nothing
                        if (live)
                          return (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                background: "rgb(0 0 0 / 40%)",
                                borderRadius: "90px",
                                padding: "4px 12px",
                                border: "1px solid #19232f",
                                boxShadow: "inset #0000004d 0 2px 4px",
                                width: "max-content",
                              }}>
                              <span className="live-pulse" />
                              <p
                                style={{
                                  margin: "0",
                                  fontSize: "12px",
                                  opacity: "0.6",
                                  fontWeight: "400",
                                  color: "white",
                                }}>
                                MATCHWEEK IS LIVE
                              </p>
                            </div>
                          );

                        // CASE 1: no players → Build Team
                        if (hasNoPlayers) {
                          return (
                            <div className="selected-team-box">
                              <div className="selected-team-status">
                                <button
                                  className="edit-btn"
                                  onClick={() => navigate(`/dashboard/league/${selectedLeagueId}/build`)}
                                  id="edit-btn-dashboard">
                                  Build Team
                                </button>
                              </div>
                            </div>
                          );
                        }

                        // CASE 2: not live & has players → Pre-Match Prep
                        return (
                          <div className="selected-team-box">
                            <div className="selected-team-status">
                              {featured && <TimeRemain startDate={featured.week_start_at} subtitle="WEEK STARTS IN" fontColor={"white"} />}

                              <button
                                className="edit-btn"
                                onClick={() => navigate(`/dashboard/league/${selectedLeagueId}/prep`)}
                                id="edit-btn-dashboard">
                                Pre-Match Prep
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    <div
                      className="selected-team"
                      style={{
                        backgroundImage: `
      url('/map-bg.png')
    `,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                        backgroundSize: "contain",
                      }}>
                      {fantasyTeam?.user_fantasy_team_players?.length > 0 ? (
                        <>
                          {fantasyTeam.user_fantasy_team_players
                            .slice()
                            .sort((a, b) => roleOrder.indexOf(a.players.role) - roleOrder.indexOf(b.players.role))
                            .map((p, i) => {
                              const proj = projectedPoints?.find((x) => x.player_id === p.player_id);
                              const latestProgress = Array.isArray(p.player_training_progress)
                                ? p.player_training_progress.sort((a, b) => new Date(b.last_trained_at) - new Date(a.last_trained_at))[0]
                                : null;
                              return (
                                <PlayerCard
                                  key={p.player_id}
                                  p={p}
                                  i={i}
                                  lvl={latestProgress}
                                  leagueId={leagueId}
                                  proj={proj}
                                  openCardId={openCardId}
                                  setOpenCardId={setOpenCardId}
                                />
                              );
                            })}
                        </>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <Loader loadingTitle={"Loading Team"} />
              )}
              <div className="top-performers-box" style={{ maxWidth: "940px" }}>
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #19232f",
                  }}>
                  <h2 style={{ borderBottom: "unset" }}>Team Performance Overview</h2>
                  <p style={{ margin: "0", color: "white", fontSize: "11px", cursor: "pointer" }} onClick={() => setShowScoring(true)}>
                    &#9432; SCORING
                  </p>
                  {showScoring && (
                    <div className="modal-overlay" onClick={() => setShowScoring(false)}>
                      <div
                        className="modal"
                        onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
                      >
                        <div className="modal-header">
                          <h2>Fantasy Scoring</h2>
                          <button onClick={() => setShowScoring(false)} className="cancel">
                            Close
                          </button>
                        </div>
                        <div className="modal-body">
                          <ul>
                            <li>
                              <strong>Kill:</strong> +3 pts
                            </li>
                            <li>
                              <strong>Assist:</strong> +2 pts
                            </li>
                            <li>
                              <strong>Death:</strong> -1 pt
                            </li>
                            <li>
                              <strong>CS:</strong> +0.02 pts
                            </li>
                          </ul>
                          <p className="note">Boosts and bonuses apply automatically when active.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="scroll-content" style={{ maxHeight: "416px" }}>
                  {fantasyTeam?.user_fantasy_team_players?.length > 0 ? (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ minWidth: "148px", fontSize: "11px", color: "rgba(255, 255, 255, 0.6)", textTransform: "uppercase" }}>
                        Expand for details
                      </span>
                      <p style={{ fontSize: "12px", color: "rgb(255 255 255 / 60%)" }}>K</p>
                      <p style={{ fontSize: "12px", color: "rgb(255 255 255 / 60%)" }}>D</p>
                      <p style={{ fontSize: "12px", color: "rgb(255 255 255 / 60%)" }}>A</p>
                      <p style={{ fontSize: "12px", color: "rgb(255 255 255 / 60%)", minWidth: "28px" }}>Pts</p>
                    </div>
                  ) : (
                    <></>
                  )}

                  {mergedStats && mergedStats.length > 0 ? (
                    mergedStats
                      ?.slice()
                      .sort((a, b) => {
                        return roleOrder.indexOf(a.role) - roleOrder.indexOf(b.role);
                      })
                      .map((p, i) => {
                        const { isLive } = getLiveOpponentInfo(p, schedule);

                        return (
                          <div key={p.player_id}>
                            <div
                              className="player-block"
                              onClick={() => navigate(`/dashboard/league/${leagueId}/player/${p.player_id}`)}
                              style={{ cursor: "pointer" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  minWidth: "140px",
                                  maxWidth: "140px",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                                className="player-name-navigate">
                                {isLive && <span className="live-pulse" />}
                                <div>
                                  <img
                                    src={`/teams/${p.team_logo}`}
                                    alt={p.team_name}
                                    className="team-logo-md"
                                    style={{ width: "20px", height: "20px" }}
                                  />
                                </div>
                                <div>
                                  <img src={`/roles/${p.role}.svg`} alt={p.role} className="team-logo-md" style={{ width: "14px", height: "14px" }} />
                                </div>
                                <div className={p.player_name.length > 10 ? "scrolling-text" : ""}>
                                  <h4>{p.player_name.toUpperCase()}</h4>
                                </div>
                              </div>

                              <div style={{ minWidth: "28px" }}>
                                <h4>{p.kills}</h4>
                              </div>
                              <div style={{ minWidth: "28px" }}>
                                <h4>{p.deaths}</h4>
                              </div>
                              <div style={{ minWidth: "28px" }}>
                                <h4>{p.assists}</h4>
                              </div>
                              <div style={{ minWidth: "40px" }}>
                                <h4 style={{ color: "rgb(240, 217, 125)", fontWeight: "900" }}>{p.boosted_points.toFixed(2)}</h4>
                              </div>
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    <>
                      {myTeam && myTeam.length === 0 ? (
                        <></>
                      ) : (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ minWidth: "148px", fontSize: "11px", color: "rgba(255, 255, 255, 0.6)", textTransform: "uppercase" }}>
                            Expand for details
                          </span>
                          <p style={{ fontSize: "12px", color: "rgb(255 255 255 / 60%)" }}>K</p>
                          <p style={{ fontSize: "12px", color: "rgb(255 255 255 / 60%)" }}>D</p>
                          <p style={{ fontSize: "12px", color: "rgb(255 255 255 / 60%)" }}>A</p>
                          <p style={{ fontSize: "12px", color: "rgb(255 255 255 / 60%)", minWidth: "28px" }}>Pts</p>
                        </div>
                      )}
                      {fantasyTeam?.user_fantasy_team_players?.length > 0 ? (
                        fantasyTeam?.user_fantasy_team_players
                          ?.slice()
                          .sort((a, b) => {
                            return roleOrder.indexOf(a.players.role) - roleOrder.indexOf(b.players.role);
                          })
                          .map((p, i) => (
                            <div
                              className="player-block"
                              key={p.player_id}
                              onClick={() => navigate(`/dashboard/league/${leagueId}/player/${p.player_id}`)}
                              style={{ cursor: "pointer" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: "200px" }}>
                                <div>
                                  <img
                                    src={`/teams/${p.players.teams.team_logo}`}
                                    alt={p.team_name}
                                    className="team-logo-md"
                                    style={{ width: "20px", height: "20px" }}
                                  />
                                </div>
                                <div>
                                  <img
                                    src={`/roles/${p.players.role}.svg`}
                                    alt={p.players.role}
                                    className="team-logo-md"
                                    style={{ width: "16px", height: "16px" }}
                                  />
                                </div>
                                <div>
                                  <h4>{p.players.name.toUpperCase()}</h4>
                                </div>
                              </div>

                              <div style={{ minWidth: "28px" }}>
                                <h4>0</h4>
                              </div>
                              <div style={{ minWidth: "28px" }}>
                                <h4>0</h4>
                              </div>
                              <div style={{ minWidth: "28px" }}>
                                <h4>0</h4>
                              </div>
                              <div style={{ minWidth: "28px" }}>
                                <h4>0</h4>
                              </div>
                            </div>
                          ))
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          {!isLocked && (
                            <>
                              {!hasStarted ? (
                                <>
                                  <p style={{ color: "#535e6c", fontSize: "14px", textAlign: "center", maxWidth: "366px" }}>
                                    The league hasn’t started yet — check back for player stats!
                                  </p>
                                  <div className="selected-team-status">
                                    <button
                                      className="edit-btn"
                                      style={{ borderRadius: "90px" }}
                                      onClick={() => navigate(`/dashboard/league/${selectedLeagueId}/build`)}>
                                      {fantasyTeam?.user_fantasy_team_players?.length > 0 ? "Update team" : "Build team"}
                                    </button>
                                  </div>
                                </>
                              ) : (
                                // Tournament already started
                                <>
                                  <img src="/3.webp" alt="empty" style={{ opacity: ".2", maxWidth: "160px" }} />
                                  <p style={{ color: "#535e6c", fontSize: "14px", textAlign: "center", maxWidth: "366px" }}>
                                    Tournament already started — but don't worry! You can still update your team.
                                  </p>
                                  <p style={{ color: "#535e6c", fontSize: "14px", textAlign: "center", maxWidth: "336px" }}>
                                    Players you pick now won’t count games that already happened.
                                  </p>
                                  <div className="selected-team-status">
                                    <button className="edit-btn" style={{ borderRadius: "90px" }} onClick={() => navigate(``)}>
                                      {fantasyTeam?.user_fantasy_team_players?.length > 0 ? "Update team" : "Build team"}
                                    </button>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {fantasyTeam?.user_fantasy_team_players?.length > 0 ? (
                    <div className="boost-performance-container">
                      <h3
                        style={{
                          minWidth: "148px",
                          fontSize: "11px",
                          color: "rgba(255, 255, 255, 0.6)",
                          textTransform: "uppercase",
                          margin: "4px 0",
                        }}>
                        Selected Items Overview
                      </h3>
                      <div className="boost-performance-box">
                        {Object.entries(totalBoostBonus).length > 0
                          ? Object.entries(totalBoostBonus).map(([boostName, boostData]) => (
                              <div key={boostName} className="boost-block">
                                <div style={{ position: "relative" }}>
                                  <div className="boost-glare" style={{ left: "unset", bottom: "unset", width: "36px", height: "36px" }}></div>
                                  {boostData.itemImage && <img src={`/boosts/${boostData.itemImage}`} alt={boostName} />}
                                </div>
                                <span style={{ color: "rgb(240, 217, 125)" }}>
                                  +{boostData.totalBonus.toFixed(1)} <b style={{ fontWeight: "400", fontSize: "10px" }}>pts</b>
                                </span>
                              </div>
                            ))
                          : fantasyTeam.user_fantasy_team_players
                              .slice()
                              .sort((a, b) => roleOrder.indexOf(a.players.role) - roleOrder.indexOf(b.players.role))
                              .map((p, i) => (
                                <div key={i} className="boost-block">
                                  <div style={{ position: "relative" }}>
                                    <div className="boost-glare" style={{ left: "unset", bottom: "unset", width: "36px", height: "36px" }}></div>
                                    <img src={`/boosts/${p.boosts.item_image}`} alt={p.boosts.boost_name} />
                                  </div>
                                  <span style={{ color: "rgb(240, 217, 125)" }}>
                                    +0 <b style={{ fontWeight: "400", fontSize: "10px" }}>pts</b>
                                  </span>
                                </div>
                              ))}
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </div>
            <div className="main-row" style={{ marginTop: "20px" }} id="mobile-main-row">
              <UpcomingFixtures fixtures={upcomingFixtures} userTeamId={userTeamId} />
              <Leaderboard leaderboard={standings} league={tournament} currentTeamId={userTeamId} leagueRewards={leagueRewards} />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Dashboard;
